'use client';

/**
 * FlowTime — отчёты.
 *
 * Графики нарисованы обычным SVG без библиотеки: данных здесь немного,
 * а внешняя зависимость ради четырёх диаграмм утяжелила бы бандл сильнее,
 * чем весит весь инструмент.
 *
 * Подписи месяцев и дней недели берутся из `Intl` по языку страницы:
 * зашитые русские сокращения выглядели бы опечаткой на английской версии.
 */

import { useMemo, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import type { FlowTimeApi } from '@/components/datahunter/use-flowtime';
import {
  MS_DAY,
  addDays,
  bestStreak,
  dayKey,
  entrySeconds,
  fmtDur,
  fmtDurAxis,
  humanDay,
  humanDayFull,
  keyToDate,
  monthsShort,
  pad2,
  startOfDay,
  streak,
  weekdaysLong,
  weekdaysShort,
} from '@/lib/flowtime-format';

const RANGES = [7, 30, 90, 365] as const;

export function FlowReports({ flow }: { flow: FlowTimeApi }) {
  const t = useTranslations('dh_flowtime_ui');
  const locale = useLocale();
  const [range, setRange] = useState<number>(30);
  const entries = flow.data.entries;

  // Названия месяцев и дней недели зависят от языка страницы, поэтому
  // считаются один раз на смену локали, а не при каждой отрисовке.
  const months = useMemo(() => monthsShort(locale), [locale]);
  const weekdays = useMemo(() => weekdaysShort(locale), [locale]);
  const weekdaysFull = useMemo(() => weekdaysLong(locale), [locale]);

  const bounds = useMemo(() => {
    const to = startOfDay() + MS_DAY;
    return { from: to - range * MS_DAY, to };
  }, [range]);

  const focus = useMemo(
    () =>
      entries.filter((e) => e.mode === 'focus' && e.start >= bounds.from && e.start < bounds.to),
    [entries, bounds],
  );

  // ---------------------------------------------------------------------
  // Показатели
  // ---------------------------------------------------------------------

  const kpi = useMemo(() => {
    const total = focus.reduce((s, e) => s + entrySeconds(e), 0);
    const prev = entries.filter(
      (e) => e.mode === 'focus' && e.start >= bounds.from - range * MS_DAY && e.start < bounds.from,
    );
    const prevTotal = prev.reduce((s, e) => s + entrySeconds(e), 0);
    const activeDays = new Set(focus.map((e) => dayKey(e.start))).size;
    const done = focus.filter((e) => e.completed).length;

    return {
      total,
      // Сравнение с прошлым периодом той же длины: абсолютные часы без
      // точки отсчёта ни о чём не говорят.
      delta: prevTotal > 0 ? Math.round(((total - prevTotal) / prevTotal) * 100) : null,
      avg: activeDays > 0 ? total / activeDays : 0,
      activeDays,
      pomos: done,
      interrupted: focus.length - done,
      rate: focus.length > 0 ? Math.round((done / focus.length) * 100) : null,
      longest: focus.reduce((m, e) => Math.max(m, entrySeconds(e)), 0),
      distractions: focus.reduce((s, e) => s + e.interruptions, 0),
      streakNow: streak(entries),
      streakBest: bestStreak(entries),
    };
  }, [focus, entries, bounds, range]);

  // ---------------------------------------------------------------------
  // Фокус по дням
  // ---------------------------------------------------------------------

  const daily = useMemo(() => {
    const buckets = new Map<string, number>();
    for (let i = 0; i < range; i++) buckets.set(dayKey(bounds.from + i * MS_DAY), 0);
    for (const e of focus) {
      const key = dayKey(e.start);
      const cur = buckets.get(key);
      if (cur !== undefined) buckets.set(key, cur + entrySeconds(e));
    }
    const values = [...buckets.entries()];
    const max = Math.max(...values.map(([, v]) => v), flow.settings.goalHours * 3600, 3600);
    return { values, max };
  }, [focus, bounds, range, flow.settings.goalHours]);

  // ---------------------------------------------------------------------
  // Проекты
  // ---------------------------------------------------------------------

  const byProject = useMemo(() => {
    const totals = new Map<string, number>();
    let grand = 0;
    for (const e of focus) {
      const key = e.projectId ?? '_none';
      totals.set(key, (totals.get(key) ?? 0) + entrySeconds(e));
      grand += entrySeconds(e);
    }
    const items = [...totals.entries()]
      .map(([id, seconds]) => {
        const project = flow.projectById(id === '_none' ? null : id);
        return {
          id,
          name: project?.name ?? t('entry_no_project'),
          color: project?.color ?? 'var(--color-text-muted)',
          seconds,
        };
      })
      .sort((a, b) => b.seconds - a.seconds);
    return { items, grand };
  }, [focus, flow, t]);

  // ---------------------------------------------------------------------
  // Часы суток
  // ---------------------------------------------------------------------

  const hourly = useMemo(() => {
    const hours = new Array<number>(24).fill(0);
    for (const e of focus) {
      // Сессия может пересекать границу часа: время делится по часам,
      // иначе полуторачасовая работа целиком падала бы в час начала.
      let cursor = e.start;
      while (cursor < e.end) {
        const d = new Date(cursor);
        const nextHour = new Date(
          d.getFullYear(),
          d.getMonth(),
          d.getDate(),
          d.getHours() + 1,
        ).getTime();
        const slice = Math.min(nextHour, e.end) - cursor;
        hours[d.getHours()] = (hours[d.getHours()] ?? 0) + slice / 1000;
        cursor += slice;
      }
    }
    const max = Math.max(...hours, 1);
    const peak = hours.indexOf(Math.max(...hours));
    return { hours, max, peak: Math.max(...hours) > 0 ? peak : null };
  }, [focus]);

  // ---------------------------------------------------------------------
  // Дни недели
  // ---------------------------------------------------------------------

  const weekly = useMemo(() => {
    const sums = new Array<number>(7).fill(0);
    const days = Array.from({ length: 7 }, () => new Set<string>());
    for (const e of focus) {
      const d = new Date(e.start).getDay();
      sums[d] = (sums[d] ?? 0) + entrySeconds(e);
      days[d]?.add(dayKey(e.start));
    }
    // Среднее за рабочий день, а не сумма: иначе понедельников в периоде
    // больше, чем суббот, и столбцы сравнивать бессмысленно.
    const avg = sums.map((s, i) => {
      const count = days[i]?.size ?? 0;
      return count > 0 ? s / count : 0;
    });
    const order = flow.settings.weekStart === 1 ? [1, 2, 3, 4, 5, 6, 0] : [0, 1, 2, 3, 4, 5, 6];
    return { avg, sums, order, max: Math.max(...avg, 1) };
  }, [focus, flow.settings.weekStart]);

  // ---------------------------------------------------------------------
  // Карта года
  // ---------------------------------------------------------------------

  const heatmap = useMemo(() => {
    const weeks = 53;
    const today = startOfDay();
    const offset = (new Date(today).getDay() - flow.settings.weekStart + 7) % 7;
    const start = addDays(today, -(offset + (weeks - 1) * 7));

    const totals = new Map<string, number>();
    for (const e of entries) {
      if (e.mode !== 'focus') continue;
      const key = dayKey(e.start);
      totals.set(key, (totals.get(key) ?? 0) + entrySeconds(e));
    }

    const goal = Math.max(flow.settings.goalHours * 3600, 1800);
    const cells: { key: string; x: number; y: number; value: number; level: number }[] = [];
    let active = 0;
    let total = 0;

    for (let w = 0; w < weeks; w++) {
      for (let d = 0; d < 7; d++) {
        const ts = addDays(start, w * 7 + d);
        if (ts > today) continue;
        const key = dayKey(ts);
        const value = totals.get(key) ?? 0;
        if (value > 0) {
          active++;
          total += value;
        }
        const ratio = Math.min(1, value / goal);
        const level = value === 0 ? 0 : ratio < 0.25 ? 1 : ratio < 0.5 ? 2 : ratio < 0.85 ? 3 : 4;
        cells.push({ key, x: w, y: d, value, level });
      }
    }

    // Подписи месяцев ставятся там, где месяц сменился.
    const monthLabels: { x: number; label: string }[] = [];
    let lastMonth = -1;
    for (let w = 0; w < weeks; w++) {
      const ts = addDays(start, w * 7);
      const m = new Date(ts).getMonth();
      if (m !== lastMonth) {
        lastMonth = m;
        monthLabels.push({ x: w, label: months[m] ?? '' });
      }
    }

    return { cells, monthLabels, active, total };
  }, [entries, flow.settings, months]);

  const hasData = entries.some((e) => e.mode === 'focus');

  if (!hasData) {
    return <p className="py-16 text-center text-[var(--color-text-muted)]">{t('reports_empty')}</p>;
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Период */}
      <div className="flex justify-end">
        <div className="inline-flex rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] p-1">
          {RANGES.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => {
                setRange(r);
              }}
              aria-pressed={range === r}
              className={`px-3 py-1.5 rounded text-xs transition-colors ${
                range === r
                  ? 'bg-[var(--color-bg-elevated)] text-[var(--color-text)] font-medium'
                  : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
              }`}
            >
              {t(`range_${String(r)}`)}
            </button>
          ))}
        </div>
      </div>

      {/* Показатели */}
      <dl className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
        {[
          {
            label: t('kpi_total'),
            value: fmtDur(kpi.total, locale),
            sub:
              kpi.delta === null
                ? t('kpi_total_nodata')
                : t('kpi_total_delta', {
                    sign: kpi.delta > 0 ? '+' : '',
                    percent: kpi.delta,
                  }),
          },
          {
            label: t('kpi_avg'),
            value: fmtDur(kpi.avg, locale),
            sub: t('kpi_avg_sub', { count: kpi.activeDays }),
          },
          {
            label: t('kpi_pomodoros'),
            value: String(kpi.pomos),
            sub: t('kpi_pomodoros_sub', { count: kpi.interrupted }),
          },
          {
            label: t('kpi_rate'),
            value: kpi.rate === null ? '—' : `${String(kpi.rate)}%`,
            sub: t('kpi_rate_sub'),
          },
          {
            label: t('kpi_streak'),
            value: String(kpi.streakNow),
            sub: t('kpi_streak_sub', { best: kpi.streakBest }),
          },
          {
            label: t('kpi_longest'),
            value: fmtDur(kpi.longest, locale),
            sub: t('kpi_longest_sub', { count: kpi.distractions }),
          },
        ].map((k) => (
          <div
            key={k.label}
            className="p-4 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-card)]"
          >
            <dt className="text-xs text-[var(--color-text-muted)] uppercase tracking-wide">
              {k.label}
            </dt>
            <dd className="text-xl font-semibold tabular-nums mt-1 leading-tight">{k.value}</dd>
            <p className="text-xs text-[var(--color-text-muted)] mt-0.5">{k.sub}</p>
          </div>
        ))}
      </dl>

      <div className="grid lg:grid-cols-2 gap-4 items-start">
        {/* Фокус по дням */}
        <Card title={t('chart_daily')}>
          <svg viewBox="0 0 640 200" className="w-full" role="img" aria-label={t('chart_daily')}>
            {[0, 0.5, 1].map((f) => {
              const y = 8 + (1 - f) * 170;
              const v = daily.max * f;
              return (
                <g key={f}>
                  <line
                    x1="44"
                    y1={y}
                    x2="634"
                    y2={y}
                    stroke="var(--color-border)"
                    strokeWidth="1"
                  />
                  <text
                    x="38"
                    y={y + 3}
                    textAnchor="end"
                    fontSize="10"
                    fill="var(--color-text-muted)"
                  >
                    {fmtDurAxis(v, locale)}
                  </text>
                </g>
              );
            })}

            {flow.settings.goalHours > 0 && flow.settings.goalHours * 3600 <= daily.max && (
              <line
                x1="44"
                y1={8 + (1 - (flow.settings.goalHours * 3600) / daily.max) * 170}
                x2="634"
                y2={8 + (1 - (flow.settings.goalHours * 3600) / daily.max) * 170}
                stroke="var(--color-warning-500)"
                strokeWidth="1.4"
                strokeDasharray="4 4"
              />
            )}

            {daily.values.map(([key, v], i) => {
              const bw = 590 / daily.values.length;
              const h = v > 0 ? Math.max(2, (v / daily.max) * 170) : 0;
              return (
                <rect
                  key={key}
                  x={44 + i * bw}
                  y={8 + 170 - h}
                  width={Math.max(1.5, bw - Math.min(4, bw * 0.25))}
                  height={h}
                  rx="2"
                  fill="var(--color-text)"
                  opacity={v > 0 ? 0.85 : 0}
                >
                  <title>{`${humanDay(key, locale)}: ${fmtDur(v, locale)}`}</title>
                </rect>
              );
            })}

            {daily.values.map(([key], i) => {
              const step = Math.max(1, Math.ceil(daily.values.length / 8));
              if (i % step !== 0 && i !== daily.values.length - 1) return null;
              const bw = 590 / daily.values.length;
              const d = keyToDate(key);
              return (
                <text
                  key={key}
                  x={44 + i * bw + bw / 2}
                  y="194"
                  textAnchor="middle"
                  fontSize="10"
                  fill="var(--color-text-muted)"
                >
                  {`${String(d.getDate())} ${months[d.getMonth()] ?? ''}`}
                </text>
              );
            })}
          </svg>
        </Card>

        {/* Проекты */}
        <Card title={t('chart_projects')}>
          {byProject.grand === 0 ? (
            <p className="text-sm text-[var(--color-text-muted)] py-8 text-center">
              {t('chart_projects_empty')}
            </p>
          ) : (
            <>
              <svg
                viewBox="0 0 240 240"
                className="w-full max-w-[220px] mx-auto"
                role="img"
                aria-label={t('chart_projects')}
              >
                {(() => {
                  let angle = -Math.PI / 2;
                  return byProject.items.map((it) => {
                    const sweep = (it.seconds / byProject.grand) * Math.PI * 2;
                    const end = angle + sweep;
                    const big = sweep > Math.PI ? 1 : 0;
                    const R = 100;
                    const r = 62;
                    const d = [
                      `M ${String(120 + R * Math.cos(angle))} ${String(120 + R * Math.sin(angle))}`,
                      `A ${String(R)} ${String(R)} 0 ${String(big)} 1 ${String(120 + R * Math.cos(end))} ${String(120 + R * Math.sin(end))}`,
                      `L ${String(120 + r * Math.cos(end))} ${String(120 + r * Math.sin(end))}`,
                      `A ${String(r)} ${String(r)} 0 ${String(big)} 0 ${String(120 + r * Math.cos(angle))} ${String(120 + r * Math.sin(angle))} Z`,
                    ].join(' ');
                    angle = end;
                    return (
                      <path key={it.id} d={d} fill={it.color} opacity="0.9">
                        <title>{`${it.name}: ${fmtDur(it.seconds, locale)}`}</title>
                      </path>
                    );
                  });
                })()}
                <text
                  x="120"
                  y="118"
                  textAnchor="middle"
                  fontSize="20"
                  fontWeight="600"
                  fill="var(--color-text)"
                >
                  {fmtDur(byProject.grand, locale)}
                </text>
                <text
                  x="120"
                  y="136"
                  textAnchor="middle"
                  fontSize="11"
                  fill="var(--color-text-muted)"
                >
                  {t('chart_projects_center')}
                </text>
              </svg>

              <ul className="flex flex-col gap-1.5 mt-4">
                {byProject.items.slice(0, 7).map((it) => (
                  <li key={it.id} className="flex items-center gap-2.5 text-sm">
                    <span
                      className="w-2 h-2 rounded-sm shrink-0"
                      style={{ background: it.color }}
                    />
                    <span className="flex-1 truncate">{it.name}</span>
                    <span className="text-xs text-[var(--color-text-muted)] tabular-nums">
                      {fmtDur(it.seconds, locale)} ·{' '}
                      {String(Math.round((it.seconds / byProject.grand) * 100))}%
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </Card>

        {/* Часы суток */}
        <Card
          title={t('chart_hours')}
          hint={
            hourly.peak === null ? '' : t('chart_hours_peak', { hour: `${pad2(hourly.peak)}:00` })
          }
        >
          <svg viewBox="0 0 640 170" className="w-full" role="img" aria-label={t('chart_hours')}>
            <line x1="32" y1="144" x2="634" y2="144" stroke="var(--color-border)" strokeWidth="1" />
            {hourly.hours.map((v, h) => {
              const bw = 602 / 24;
              const bh = v > 0 ? Math.max(2, (v / hourly.max) * 138) : 0;
              return (
                <g key={h}>
                  <rect
                    x={32 + h * bw}
                    y={144 - bh}
                    width={bw - 3}
                    height={bh}
                    rx="2"
                    fill="var(--color-text)"
                    opacity={v > 0 ? 0.25 + 0.65 * (v / hourly.max) : 0}
                  >
                    <title>{`${pad2(h)}:00 — ${fmtDur(v, locale)}`}</title>
                  </rect>
                  {h % 3 === 0 && (
                    <text
                      x={32 + h * bw + bw / 2}
                      y="162"
                      textAnchor="middle"
                      fontSize="10"
                      fill="var(--color-text-muted)"
                    >
                      {pad2(h)}
                    </text>
                  )}
                </g>
              );
            })}
          </svg>
        </Card>

        {/* Дни недели */}
        <Card title={t('chart_weekdays')} hint={t('chart_weekdays_hint')}>
          <svg viewBox="0 0 640 170" className="w-full" role="img" aria-label={t('chart_weekdays')}>
            <line x1="32" y1="144" x2="634" y2="144" stroke="var(--color-border)" strokeWidth="1" />
            {weekly.order.map((d, i) => {
              const bw = 602 / 7;
              const v = weekly.avg[d] ?? 0;
              const bh = v > 0 ? Math.max(2, (v / weekly.max) * 138) : 0;
              return (
                <g key={d}>
                  <rect
                    x={32 + i * bw + bw * 0.15}
                    y={144 - bh}
                    width={bw * 0.7}
                    height={bh}
                    rx="3"
                    fill="var(--color-text)"
                    opacity={v > 0 ? 0.3 + 0.6 * (v / weekly.max) : 0}
                  >
                    <title>
                      {t('tip_avg_total', {
                        day: weekdaysFull[d] ?? '',
                        avg: fmtDur(v, locale),
                        total: fmtDur(weekly.sums[d] ?? 0, locale),
                      })}
                    </title>
                  </rect>
                  <text
                    x={32 + i * bw + bw / 2}
                    y="162"
                    textAnchor="middle"
                    fontSize="10"
                    fill="var(--color-text-muted)"
                  >
                    {weekdays[d] ?? ''}
                  </text>
                </g>
              );
            })}
          </svg>
        </Card>
      </div>

      {/* Год */}
      <Card
        title={t('chart_year')}
        hint={
          heatmap.active > 0
            ? t('chart_year_hint', {
                days: heatmap.active,
                total: fmtDur(heatmap.total, locale),
              })
            : ''
        }
      >
        <div className="overflow-x-auto pb-1">
          <svg
            viewBox="0 0 823 121"
            className="min-w-[760px] w-full"
            role="img"
            aria-label={t('chart_year')}
          >
            {heatmap.monthLabels.map((m) => (
              <text
                key={`${m.label}-${String(m.x)}`}
                x={26 + m.x * 15}
                y="10"
                fontSize="9"
                fill="var(--color-text-muted)"
              >
                {m.label}
              </text>
            ))}
            {[0, 2, 4].map((i) => (
              <text key={i} x="0" y={16 + i * 15 + 10} fontSize="9" fill="var(--color-text-muted)">
                {weekdays[(flow.settings.weekStart + i) % 7] ?? ''}
              </text>
            ))}
            {heatmap.cells.map((c) => (
              <rect
                key={c.key}
                x={26 + c.x * 15}
                y={16 + c.y * 15}
                width="12"
                height="12"
                rx="2"
                fill={c.level === 0 ? 'var(--color-bg-elevated)' : 'var(--color-text)'}
                opacity={[1, 0.28, 0.5, 0.74, 1][c.level] ?? 1}
              >
                <title>{`${humanDayFull(c.key, locale)}: ${c.value > 0 ? fmtDur(c.value, locale) : t('tip_no_records')}`}</title>
              </rect>
            ))}
          </svg>
        </div>
      </Card>
    </div>
  );
}

function Card({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="p-5 rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-card)]">
      <header className="flex items-baseline justify-between gap-3 mb-4">
        <h3 className="text-sm font-semibold">{title}</h3>
        {hint && <span className="text-xs text-[var(--color-text-muted)]">{hint}</span>}
      </header>
      {children}
    </section>
  );
}
